import { Box, Modal, Select, Text, Textarea, Toggle } from '@tocktix/pantry';
import { ChangeEvent, FunctionComponent, useState } from 'react';
import { DeactivationReason } from 'tools/bff/generated/user/user_administration_pb';
import { Form, Formik, FormikErrors } from 'formik';
import { REASON_OPTIONS } from 'tools/users/deactivationReasons';
import { useActions } from 'utils/connect';
import { userClient } from 'tools/bff/client';

interface DeactivateUserFormValues {
  reasonCode: string;
  note: string;
  sendNotification: boolean;
}

interface DeactivateUserModalProps {
  uuid: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}

const validate = (values: DeactivateUserFormValues) => {
  const errors: FormikErrors<DeactivateUserFormValues> = {};
  if (!values.reasonCode) {
    errors.reasonCode = 'Reason is required';
  }
  return errors;
};

const DeactivateUserModal: FunctionComponent<DeactivateUserModalProps> = ({
  uuid,
  open,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState<string | null>(null);
  const { actions } = useActions();

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmission = (values: DeactivateUserFormValues) => {
    return userClient
      .deactivateUsers({
        uuids: [uuid],
        // Structured token -> Resy deactivation_reason (Resy only).
        reasonCode: Number(values.reasonCode) as DeactivationReason,
        // Free-text note -> the GDUS lockout reason and Resy deactivation_description (both).
        reason: values.note.trim(),
        ...(values.sendNotification ? { sendNotification: true } : {}),
      })
      .then(onSubmit)
      .then(handleClose)
      .then(() => actions.toast.showInfo('Account deactivated'))
      .catch((reason) => {
        setError('Error deactivating user: ' + reason);
        actions.toast.showError('Error deactivating user');
      });
  };

  return (
    <Formik<DeactivateUserFormValues>
      initialValues={{
        reasonCode: '',
        note: '',
        sendNotification: false,
      }}
      validate={validate}
      onSubmit={handleSubmission}
    >
      {({ dirty, errors, handleSubmit, isSubmitting, isValid, setFieldValue, touched, values }) => (
        <Form id="deactivate-user-form" data-testid="deactivate-user-form">
          <Modal
            id="deactivate-user-modal"
            open={open}
            onClose={handleClose}
            size="md"
            data-testid="deactivate-user-modal"
          >
            <Modal.Header title="Deactivate account" onClose={handleClose} border />
            <Modal.Body>
              <Box display="flex" flexDirection="column" gap={200}>
                <Text variant="body" color="fgSubtle">
                  This will deactivate the account and prevent the user from using it until it is
                  reactivated.
                </Text>
                <Select
                  id="deactivate-user-reason"
                  label="Reason"
                  placeholder="Select a reason"
                  value={values.reasonCode}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                    setFieldValue('reasonCode', event.target.value)
                  }
                  required
                  fullWidth
                  data-testid="deactivate-user-reason-select"
                  validationStatus={touched.reasonCode && errors.reasonCode ? 'error' : undefined}
                  validationMessage={touched.reasonCode ? errors.reasonCode : undefined}
                >
                  {REASON_OPTIONS.map(({ value, label }) => (
                    <Select.Option key={value} value={String(value)}>
                      {label}
                    </Select.Option>
                  ))}
                </Select>
                <Textarea
                  id="deactivate-user-note"
                  label="Note (optional)"
                  hint="Free-text note saved on the account lockout and the Resy deactivation record."
                  value={values.note}
                  onChange={(event) => setFieldValue('note', event.target.value)}
                  fullWidth
                  data-testid="deactivate-user-note-input"
                />
                <Toggle
                  id="deactivate-user-notify"
                  label="Notify user"
                  checked={values.sendNotification}
                  onChange={(event) => setFieldValue('sendNotification', event.target.checked)}
                  data-testid="deactivate-user-notify-toggle"
                />
                {error && (
                  <Text variant="body" color="fgDangerBase">
                    {error}
                  </Text>
                )}
              </Box>
            </Modal.Body>
            <Modal.Footer
              actions={{
                primary: {
                  label: isSubmitting ? 'Deactivating...' : 'Deactivate',
                  danger: true,
                  type: 'submit',
                  disabled: !dirty || !isValid || isSubmitting,
                  onClick: () => handleSubmit(),
                },
                cancel: {
                  label: 'Cancel',
                  onClick: handleClose,
                },
              }}
            />
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default DeactivateUserModal;
